import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CalendarTasksModal } from '../../components/calendars/CalendarTasksModal';
import {
  getTasksCalendarEventColor,
  isTasksCalendarStatusVisible,
  isTasksCalendarTaskKind,
  startOfDay,
  TASKS_CALENDAR_ID,
} from '../../components/calendars/calendarUtils';
import { CalendarWeekView } from '../../components/calendars/CalendarWeekView';
import { TasksCalendarVisibilityFilters } from '../../components/calendars/TasksCalendarVisibilityFilters';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { PlanningExtendDeadlineModal } from '../../components/strategic-planning/PlanningExtendDeadlineModal';
import { MyTaskDetailModal } from '../../components/tasks/MyTaskDetailModal';
import {
  MyTasksKanbanBoard,
  MyTasksKanbanColumnId,
} from '../../components/tasks/MyTasksKanbanBoard';
import {
  MyTasksFiltersPanel,
  MyTasksKindFilter,
  rangeForTasksPreset,
} from '../../components/tasks/MyTasksFiltersPanel';
import { usePermissions } from '../../contexts/PermissionsContext';
import { CalendarPlottedEvent } from '../../services/enterpriseCalendarsApi';
import {
  fetchMyTeams,
  ProfileTeamMembership,
} from '../../services/myCollaboratorApi';
import {
  assignMyTask,
  cancelMyTask,
  createMyTask,
  evaluateMyTask,
  extendMyTaskDeadline,
  fetchMyTaskCreateContext,
  fetchMyTasksScope,
  finishMyTask,
  listMyTaskEvents,
  listMyTasks,
  MyTaskListItem,
  pauseMyTask,
  searchMyTasksUsers,
  startMyTask,
  TasksScope,
} from '../../services/myTasksApi';
import { listTeamMembers } from '../../services/teamsApi';
import { TasksCalendarUser } from '../../services/tasksCalendarApi';
import { formatPlanningTaskStatusLabel } from '../../types/PlanningTaskStatus';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { MyTasksPanelMode } from './MyTasksPanelMode';

type TasksViewId = 'list' | 'kanban' | 'calendar';

type MyTasksPageProps = {
  mode?: MyTasksPanelMode;
};

const TASK_VIEW_TABS: SettingsTabItem<TasksViewId>[] = [
  { id: 'list', label: 'Lista' },
  { id: 'kanban', label: 'Kanban' },
  { id: 'calendar', label: 'Calendário' },
];

const GOALS_VIEW_TABS: SettingsTabItem<TasksViewId>[] = [
  { id: 'list', label: 'Lista' },
  { id: 'kanban', label: 'Kanban' },
];

function matchesPanelKind(kind: string | undefined, mode: MyTasksPanelMode): boolean {
  if (mode === 'goals-milestones') {
    return kind === 'Meta' || kind === 'Marco';
  }
  return !kind || kind === 'Tarefa';
}

function emptyEvaluationForm(kind?: string) {
  return {
    result: kind === 'Meta' ? 'NotExecuted' : 'Executed',
    quality: 'Good',
    deadline: 'OnTime',
    impact: 'Medium',
    lessonsLearned: '',
    notes: '',
  };
}

function initialDateRange(): { from: string; to: string } {
  return rangeForTasksPreset('week');
}

function kindLabel(kind?: string): string {
  switch (kind) {
    case 'Meta':
      return 'Meta';
    case 'Marco':
      return 'Marco';
    case 'Tarefa':
      return 'Tarefa';
    default:
      return kind || 'Item';
  }
}

function statusForColumn(column: MyTasksKanbanColumnId): string {
  switch (column) {
    case 'Doing':
      return 'InProgress';
    case 'Done':
      return 'Completed';
    case 'ToDo':
      return 'Overdue';
    default:
      return 'Planned';
  }
}

export function MyTasksPage({ mode = 'tasks' }: MyTasksPageProps) {
  const { canAccessModule, isOrganizationAdmin } = usePermissions();
  const isGoalsPanel = mode === 'goals-milestones';
  const canAccessGoalsPanel = canAccessModule('people.my-team') || isOrganizationAdmin;
  const viewTabs = isGoalsPanel ? GOALS_VIEW_TABS : TASK_VIEW_TABS;

  const [view, setView] = useState<TasksViewId>('kanban');
  const [fromDate, setFromDate] = useState(() => initialDateRange().from);
  const [toDate, setToDate] = useState(() => initialDateRange().to);
  const [statusFilter, setStatusFilter] = useState('');
  const [kindFilter, setKindFilter] = useState<MyTasksKindFilter>('');
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showCancelledTasks, setShowCancelledTasks] = useState(false);
  const [scope, setScope] = useState<TasksScope | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teams, setTeams] = useState<ProfileTeamMembership[]>([]);
  const [teamMemberIds, setTeamMemberIds] = useState<string[]>([]);
  const [extraUsers, setExtraUsers] = useState<TasksCalendarUser[]>([]);
  const [removedUsers, setRemovedUsers] = useState<TasksCalendarUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TasksCalendarUser[]>([]);
  const [items, setItems] = useState<MyTaskListItem[]>([]);
  const [events, setEvents] = useState<CalendarPlottedEvent[]>([]);
  const [extendDeadlineTask, setExtendDeadlineTask] = useState<MyTaskListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MyTaskListItem | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [fulfillmentResult, setFulfillmentResult] = useState('NotFulfilled');
  const [evaluationForm, setEvaluationForm] = useState(emptyEvaluationForm);
  const [saving, setSaving] = useState(false);

  const assignableUsers = useMemo(() => {
    const map = new Map<string, TasksCalendarUser>();
    for (const user of scope?.defaultUsers ?? []) {
      map.set(user.userId, user);
    }
    for (const user of extraUsers) {
      map.set(user.userId, user);
    }
    return Array.from(map.values());
  }, [scope, extraUsers]);

  const visibleUsers = useMemo(() => {
    const removedIds = new Set(removedUsers.map((user) => user.userId));
    return assignableUsers.filter((user) => !removedIds.has(user.userId));
  }, [assignableUsers, removedUsers]);

  const filterUserIds = useMemo(() => {
    const visibleIds = new Set(visibleUsers.map((user) => user.userId));
    let ids = selectedUserIds.filter((id) => visibleIds.has(id));

    if (selectedTeamIds.length > 0 && teamMemberIds.length > 0) {
      const allowed = new Set(teamMemberIds);
      ids = ids.filter((id) => allowed.has(id));
    }

    return ids;
  }, [selectedUserIds, visibleUsers, selectedTeamIds, teamMemberIds]);

  const visibleItems = useMemo(() => {
    if (filterUserIds.length === 0) return [];
    const allowed = new Set(filterUserIds);
    return items.filter(
      (item) =>
        matchesPanelKind(item.kind, mode)
        && (!kindFilter || item.kind === kindFilter)
        && (!item.ownerUserId || allowed.has(item.ownerUserId)),
    );
  }, [filterUserIds, items, kindFilter, mode]);

  useEffect(() => {
    if (isGoalsPanel && view === 'calendar') {
      setView('kanban');
    }
  }, [isGoalsPanel, view]);

  const visibleEvents = useMemo(() => {
    if (filterUserIds.length === 0) return [];
    const allowed = new Set(filterUserIds);
    return events.filter((event) => {
      if (event.ownerUserId && !allowed.has(event.ownerUserId)) return false;
      if (statusFilter) return event.taskStatus === statusFilter;
      return isTasksCalendarStatusVisible(event.taskStatus, showCompletedTasks, showCancelledTasks);
    });
  }, [filterUserIds, events, statusFilter, showCompletedTasks, showCancelledTasks]);

  const searchSuggestions = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (term.length < 2) return [];

    const visibleIds = new Set(visibleUsers.map((user) => user.userId));
    const byId = new Map<string, TasksCalendarUser>();
    for (const user of removedUsers) {
      const haystack = `${user.name} ${user.email ?? ''}`.toLowerCase();
      if (!visibleIds.has(user.userId) && haystack.includes(term)) {
        byId.set(user.userId, user);
      }
    }
    for (const user of searchResults) {
      if (!visibleIds.has(user.userId)) {
        byId.set(user.userId, user);
      }
    }
    return Array.from(byId.values());
  }, [removedUsers, searchQuery, searchResults, visibleUsers]);

  const dateRange = useMemo(() => ({ from: fromDate, to: toDate }), [fromDate, toDate]);

  const loadScope = useCallback(async () => {
    const [next, myTeams] = await Promise.all([
      fetchMyTasksScope(),
      fetchMyTeams().catch(() => [] as ProfileTeamMembership[]),
    ]);
    setScope(next);
    setTeams(myTeams);
    setSelectedUserIds(next.defaultUsers.map((user) => user.userId));
  }, []);

  const reload = useCallback(async () => {
    if (filterUserIds.length === 0) {
      setItems([]);
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (view === 'calendar') {
        const raw = await listMyTaskEvents({
          from: dateRange.from,
          to: dateRange.to,
          userIds: filterUserIds,
        });
        setEvents(
          raw.filter((event) => isTasksCalendarTaskKind(event.kind)).map((event) => {
            const startDate = event.plannedStartDate ?? event.plannedEndDate ?? dateRange.from;
            return {
              id: event.id,
              name: event.ownerUserName ? `${event.title} (${event.ownerUserName})` : event.title,
              description: event.description,
              type: 'Event',
              eventDate: startDate,
              isAllDay: true,
              recurrenceType: 'None',
              isRecurringYearly: false,
              excludeFromSla: false,
              scope: 'tasks',
              createdAtUtc: startDate,
              updatedAtUtc: startDate,
              calendarId: TASKS_CALENDAR_ID,
              calendarName: 'Tarefas',
              color: getTasksCalendarEventColor(event.status),
              eventSource: 'tasks' as const,
              taskEndDate: event.plannedEndDate,
              workItemId: event.id,
              ownerUserId: event.ownerUserId,
              ownerUserName: event.ownerUserName,
              taskStatus: event.status,
              taskPriority: event.priority,
              taskTitle: event.title,
              metaTitle: event.metaTitle,
              marcoTitle: event.marcoTitle,
            };
          }),
        );
        setItems([]);
      } else {
        const result = await listMyTasks({
          from: dateRange.from,
          to: dateRange.to,
          userIds: filterUserIds,
          status: statusFilter || undefined,
        });
        setItems(result.items);
        setEvents([]);
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar tarefas.'));
      setItems([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [view, dateRange, filterUserIds, statusFilter]);

  useEffect(() => {
    void loadScope().catch(() => toast.error('Falha ao carregar escopo de tarefas.'));
  }, [loadScope]);

  useEffect(() => {
    setEvaluationForm(emptyEvaluationForm(selectedTask?.kind));
  }, [selectedTask?.id, selectedTask?.kind]);

  useEffect(() => {
    if (!scope) return;
    void reload();
  }, [scope, reload]);

  useEffect(() => {
    if (selectedTeamIds.length === 0) {
      setTeamMemberIds([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      const memberIdSet = new Set<string>();
      await Promise.all(
        selectedTeamIds.map(async (teamId) => {
          try {
            const members = await listTeamMembers(teamId);
            for (const member of members) {
              memberIdSet.add(member.userId);
            }
          } catch {
            // Sem permissão de equipes: ignora filtro de membros.
          }
        }),
      );
      if (!cancelled) {
        setTeamMemberIds(Array.from(memberIdSet));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedTeamIds]);

  useEffect(() => {
    if (!scope?.canSearchCompanyUsers || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const handle = window.setTimeout(() => {
      void searchMyTasksUsers(searchQuery.trim())
        .then(setSearchResults)
        .catch(() => setSearchResults([]));
    }, 300);

    return () => window.clearTimeout(handle);
  }, [searchQuery, scope?.canSearchCompanyUsers]);

  function toggleUser(userId: string): void {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  }

  function clearFilters(): void {
    const range = rangeForTasksPreset('week');
    setFromDate(range.from);
    setToDate(range.to);
    setStatusFilter('');
    setKindFilter('');
    setSelectedTeamIds([]);
    setRemovedUsers([]);
    setExtraUsers([]);
    setSearchQuery('');
    setSearchResults([]);
    if (scope) {
      setSelectedUserIds(scope.defaultUsers.map((user) => user.userId));
    }
  }

  function removeFilterUser(user: TasksCalendarUser): void {
    if (user.isSelf) return;

    setRemovedUsers((current) =>
      current.some((item) => item.userId === user.userId) ? current : [...current, user],
    );
    setSelectedUserIds((current) => current.filter((id) => id !== user.userId));
    setExtraUsers((current) => current.filter((item) => item.userId !== user.userId));
  }

  function addExtraUser(user: TasksCalendarUser): void {
    setRemovedUsers((current) => current.filter((item) => item.userId !== user.userId));
    setExtraUsers((current) =>
      current.some((item) => item.userId === user.userId) ? current : [...current, user],
    );
    setSelectedUserIds((current) =>
      current.includes(user.userId) ? current : [...current, user.userId],
    );
    setSearchQuery('');
    setSearchResults([]);
  }

  async function handleKanbanMove(item: MyTaskListItem, column: MyTasksKanbanColumnId): Promise<void> {
    const targetStatus = statusForColumn(column);
    if (item.status === targetStatus) return;

    const previous = items;
    setMovingId(item.id);
    setItems((current) =>
      current.map((entry) => (entry.id === item.id ? { ...entry, status: targetStatus } : entry)),
    );

    try {
      if (column === 'Doing') {
        if (!item.canStart) throw new Error('Não é possível iniciar este item.');
        await startMyTask(item.id);
      } else if (column === 'Backlog' || column === 'ToDo') {
        if (item.status === 'InProgress') {
          if (!item.canPause) throw new Error('Não é possível pausar este item.');
          await pauseMyTask(item.id);
        } else if (column === 'ToDo' && item.status === 'Planned') {
          toast.info('Itens só entram em To Do quando ficam atrasados.');
          setItems(previous);
          return;
        } else {
          throw new Error('Movimento não suportado para este status.');
        }
      } else if (column === 'Done') {
        if (!item.canFinish) throw new Error('Só tarefas em andamento podem ser concluídas no quadro.');
        await finishMyTask(item.id, 'Fulfilled');
      }

      toast.success('Status atualizado.');
      await reload();
    } catch (error) {
      setItems(previous);
      toast.error(getRequestErrorMessage(error, 'Falha ao mover item no kanban.'));
    } finally {
      setMovingId(null);
    }
  }

  async function handleEvaluate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedTask?.canEvaluate) return;
    setSaving(true);
    try {
      await evaluateMyTask(selectedTask.id, evaluationForm);
      toast.success('Avaliação salva.');
      setSelectedTask(null);
      await reload();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao avaliar tarefa.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign(ownerUserId: string): Promise<void> {
    if (!selectedTask?.canAssign || !ownerUserId) return;
    setSaving(true);
    try {
      await assignMyTask(selectedTask.id, ownerUserId);
      toast.success('Responsável atribuído.');
      setSelectedTask(null);
      await reload();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atribuir responsável.'));
    } finally {
      setSaving(false);
    }
  }

  if (isGoalsPanel && !canAccessGoalsPanel) {
    return <Navigate to="/tasks" replace />;
  }

  return (
    <PageShell>
      <PageHeader
        title={isGoalsPanel ? 'Metas e Marcos' : 'Tarefas'}
        description={
          isGoalsPanel
            ? 'Acompanhe e avalie metas e marcos da sua equipe.'
            : 'Suas tarefas e da equipe. Foque na execução do dia a dia.'
        }
        actions={
          isGoalsPanel ? undefined : (
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/tasks/inventory" className="erp-btn-secondary">
                Inventário
              </Link>
              <button type="button" className="erp-btn-primary" onClick={() => setCreateOpen(true)}>
                Nova tarefa
              </button>
            </div>
          )
        }
      />

      <MyTasksFiltersPanel
        fromDate={fromDate}
        toDate={toDate}
        statusFilter={statusFilter}
        kindFilter={kindFilter}
        showKindFilter={isGoalsPanel}
        teams={teams}
        selectedTeamIds={selectedTeamIds}
        visibleUsers={visibleUsers}
        selectedUserIds={selectedUserIds}
        searchQuery={searchQuery}
        searchSuggestions={searchSuggestions}
        canSearchPeople={Boolean(scope?.canSearchCompanyUsers)}
        onApplyRange={(from, to) => {
          setFromDate(from);
          setToDate(to);
        }}
        onStatusChange={setStatusFilter}
        onKindChange={setKindFilter}
        onTeamFilterChange={setSelectedTeamIds}
        onToggleUser={toggleUser}
        onRemoveUser={removeFilterUser}
        onAddUser={addExtraUser}
        onSearchQueryChange={setSearchQuery}
        onClear={clearFilters}
      />

      <SettingsTabBar tabs={viewTabs} activeTab={view} onChange={setView} />

      {loading ? (
        <p className="text-secondary-500">
          {isGoalsPanel ? 'Carregando metas e marcos...' : 'Carregando tarefas...'}
        </p>
      ) : view === 'list' ? (
        <section className="rounded-xl border border-secondary-200 bg-white p-4">
          <div className="overflow-x-auto">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Título</th>
                  <th>Responsável</th>
                  <th>Status</th>
                  <th>Prazo</th>
                  <th>Avaliação</th>
                  <th className="w-40">Ações</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-secondary-500">
                      {isGoalsPanel
                        ? kindFilter === 'Meta'
                          ? 'Nenhuma meta no período.'
                          : kindFilter === 'Marco'
                            ? 'Nenhum marco no período.'
                            : 'Nenhuma meta ou marco no período.'
                        : 'Nenhuma tarefa no período.'}
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item) => (
                    <tr key={item.id}>
                      <td>{kindLabel(item.kind)}</td>
                      <td className="font-medium text-secondary-900">{item.title}</td>
                      <td>{item.ownerUserName || 'Não atribuído'}</td>
                      <td>
                        <CodedStatusBadge
                          status={item.status}
                          label={formatPlanningTaskStatusLabel(item.status)}
                        />
                      </td>
                      <td>{item.plannedEndDate ? formatDateBr(item.plannedEndDate) : '—'}</td>
                      <td>
                        {item.isEvaluated ? 'Avaliada' : item.canEvaluate ? 'Pendente' : '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="erp-btn-secondary text-sm"
                          onClick={() => {
                            setSelectedTask(item);
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : view === 'kanban' ? (
        <MyTasksKanbanBoard
          items={visibleItems}
          movingId={movingId}
          onSelect={(item) => {
            setSelectedTask(item);
          }}
          onCreate={isGoalsPanel ? undefined : () => setCreateOpen(true)}
          onMove={(item, column) => {
            void handleKanbanMove(item, column);
          }}
        />
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl border border-secondary-200 bg-white px-4 py-3">
            <p className="text-sm font-medium text-secondary-800">No calendário</p>
            <p className="mb-2 text-xs text-secondary-500">
              Esta vista lista somente tarefas pendentes, em andamento e atrasadas.
              {statusFilter ? ' O filtro de status acima está restringindo esta vista.' : ''}
            </p>
            <TasksCalendarVisibilityFilters
              className="flex flex-wrap gap-4"
              showCompleted={showCompletedTasks}
              showCancelled={showCancelledTasks}
              disabled={Boolean(statusFilter)}
              onShowCompletedChange={setShowCompletedTasks}
              onShowCancelledChange={setShowCancelledTasks}
            />
          </div>
          <div className="h-[520px]">
          <CalendarWeekView
            anchorDate={startOfDay(new Date(`${fromDate}T00:00:00`))}
            events={visibleEvents}
            onDayClick={() => setCreateOpen(true)}
            onEventClick={(event) => {
              const found = visibleItems.find((item) => item.id === event.id);
              if (found) {
                setSelectedTask(found);
              } else {
                void listMyTasks({
                  from: dateRange.from,
                  to: dateRange.to,
                  userIds: filterUserIds,
                  status: statusFilter || undefined,
                }).then((result) => {
                  const match = result.items.find((item) => item.id === event.id) ?? null;
                  setSelectedTask(match);
                });
              }
            }}
          />
          </div>
        </div>
      )}

      <CalendarTasksModal
        open={createOpen}
        mode="create"
        assignableUsers={assignableUsers}
        defaultOwnerUserId={assignableUsers.find((user) => user.isSelf)?.userId}
        loadCreateContext={fetchMyTaskCreateContext}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          try {
            await createMyTask(payload);
            toast.success('Tarefa criada.');
            setCreateOpen(false);
            await reload();
          } catch (error) {
            toast.error(getRequestErrorMessage(error, 'Falha ao criar tarefa.'));
            throw error;
          }
        }}
      />

      {selectedTask ? (
        <MyTaskDetailModal
          task={selectedTask}
          saving={saving}
          fulfillmentResult={fulfillmentResult}
          evaluationForm={evaluationForm}
          onClose={() => setSelectedTask(null)}
          onFulfillmentChange={setFulfillmentResult}
          onEvaluationChange={(patch) =>
            setEvaluationForm((current) => ({ ...current, ...patch }))
          }
          onAssign={(ownerUserId) => void handleAssign(ownerUserId)}
          onStart={() =>
            void (async () => {
              setSaving(true);
              try {
                await startMyTask(selectedTask.id);
                toast.success('Execução iniciada.');
                setSelectedTask(null);
                await reload();
              } catch (error) {
                toast.error(getRequestErrorMessage(error, 'Falha ao iniciar.'));
              } finally {
                setSaving(false);
              }
            })()
          }
          onPause={() =>
            void (async () => {
              setSaving(true);
              try {
                await pauseMyTask(selectedTask.id);
                toast.success('Tarefa pausada.');
                setSelectedTask(null);
                await reload();
              } catch (error) {
                toast.error(getRequestErrorMessage(error, 'Falha ao pausar.'));
              } finally {
                setSaving(false);
              }
            })()
          }
          onExtendDeadline={() => setExtendDeadlineTask(selectedTask)}
          onCancel={() =>
            void (async () => {
              const reason = window.prompt('Motivo do cancelamento');
              if (!reason?.trim()) {
                toast.error('Informe o motivo do cancelamento.');
                return;
              }
              setSaving(true);
              try {
                await cancelMyTask(selectedTask.id, reason.trim());
                toast.success('Cancelado.');
                setSelectedTask(null);
                await reload();
              } catch (error) {
                toast.error(getRequestErrorMessage(error, 'Falha ao cancelar.'));
              } finally {
                setSaving(false);
              }
            })()
          }
          onFinish={() =>
            void (async () => {
              setSaving(true);
              try {
                await finishMyTask(selectedTask.id, fulfillmentResult);
                toast.success(
                  selectedTask.kind === 'Meta' || selectedTask.kind === 'Marco'
                    ? 'Avaliação concluída.'
                    : 'Tarefa concluída.',
                );
                setSelectedTask(null);
                await reload();
              } catch (error) {
                toast.error(getRequestErrorMessage(error, 'Falha ao concluir.'));
              } finally {
                setSaving(false);
              }
            })()
          }
          onEvaluate={(event) => void handleEvaluate(event)}
          onDuplicated={() => {
            setSelectedTask(null);
            void reload();
          }}
          onAreasChange={(areas) => {
            setSelectedTask((current) => (current ? { ...current, areas } : current));
            void reload();
          }}
        />
      ) : null}

      {extendDeadlineTask ? (
        <PlanningExtendDeadlineModal
          open
          currentEndDate={extendDeadlineTask.plannedEndDate ?? undefined}
          onClose={() => setExtendDeadlineTask(null)}
          onSubmit={async (payload) => {
            try {
              await extendMyTaskDeadline(extendDeadlineTask.id, payload);
              toast.success('Prazo estendido. A tarefa foi atualizada com a nova data.');
              setExtendDeadlineTask(null);
              setSelectedTask(null);
              await reload();
            } catch (error) {
              toast.error(getRequestErrorMessage(error, 'Falha ao estender prazo.'));
              throw error;
            }
          }}
        />
      ) : null}
    </PageShell>
  );
}
